import React, { useMemo, useState } from 'react'

import { Col, Collapse, Table, Typography } from 'antd'
import { EyeFilled, EyeInvisibleFilled, LineChartOutlined } from '@ant-design/icons'

import chroma from 'chroma-js'

import { PopoverHexPicker } from '../PopoverHexPicker/PopoverHexPicker'
import { VTKLayerProperties } from '../VTKLayerProperties/VTKLayerProperties'

import { VTKLayerMenuProps } from './VTKLayerMenu.types'

import './VTKLayerMenu.scss'

interface Rec {
  id: string
  name: string
  hasData: boolean
  color: string
  visibility: boolean
  opacity: number
}

export const VTKLayerMenu: React.FunctionComponent<VTKLayerMenuProps> = ({ state, dispatch }) => {
  //array of ids
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const layers = useMemo(() => {
    if (!state || !state.scene) return
    return state.scene.map(({ id, name, state: actor }) => {
      const [r, g, b] = actor.property.diffuseColor
      const { pointData, cellData } = actor.mapper.inputData
      return ({
        id: id,
        name: name,
        hasData: pointData.numberOfArrays > 0 || cellData.numberOfArrays > 0,
        color: chroma.gl(r, g, b),
        visibility: actor.visibility,
        opacity: actor.property.opacity,
      })
    })
  }, [state])

  const columns = [
    {
      visibility: 'Visibility',
      dataIndex: 'visibility',
      className: 'pollination-column',
      render: (vis: boolean, rec: Rec, index: number) => {
        return vis ?
          <EyeFilled onClick={(e) => {
            e.stopPropagation()
            dispatch({ type: 'visibility', ids: selectedRowKeys.includes(rec.id) ? selectedRowKeys : [rec.id], value: false })
          }}
          /> :
          <EyeInvisibleFilled onClick={(e) => {
            e.stopPropagation()
            dispatch({ type: 'visibility', ids: selectedRowKeys.includes(rec.id) ? selectedRowKeys : [rec.id], value: true })
          }}
          style={{ color: '#8c8c8c' }}
          />
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      className: 'pollination-column',
      render: (name: string, rec: Rec, index: number) => {
        const displayName = name.split('::')
        return (
          <Typography.Text 
            style={{ userSelect: 'none' }}
            type={!rec.visibility ? 'secondary' : undefined}
          >
            {displayName.length > 1 ? displayName[1] : displayName[0]}
          </Typography.Text>
        )
      }
    },
    {
      title: 'Color',
      dataIndex: 'color',
      className: 'pollination-column',
      render: (color: string, rec: Rec) => (
        rec.hasData ?
          <div style={{
            width: 22,
            height: 22, border: 'solid 1px #F0F0F0', borderRadius: 2, display: 'flex', justifyContent: 'center',
            alignItems: 'center', margin: '8px auto', marginRight: '0px'
          }}
          >
            <LineChartOutlined />
          </div>
          :
          <PopoverHexPicker color={color}
            opacity={rec.opacity}
            swatchStyle={{ marginRight: 0 }}
            colorCallback={(value: string) => dispatch({
              type: 'color',
              ids: selectedRowKeys.length && selectedRowKeys.includes(rec.id) ? selectedRowKeys : [rec.id],
              value
            })}
            opacityCallback={(value: number) => dispatch({
              type: 'opacity',
              ids: selectedRowKeys.length && selectedRowKeys.includes(rec.id) ? selectedRowKeys : [rec.id],
              value
            })}
          />
      )
    },
  ]

  const handleRowClick = (record: Rec, event: any) => {
    const sel = [...selectedRowKeys]
    const index = sel.indexOf(record.id)
    // select range
    if (event.shiftKey) {
      if (!sel.length) {
        setSelectedRowKeys([record.id])
        return
      }
      sel.push(record.id)

      // find the min and max layerIndex for selected scene items
      const indices = sel.reduce((p: number[], c: string, i: number, a: string[]) => {
        const lIndex = state.scene.findIndex(v => v.id === c)
        if (lIndex < p[0]) p[0] = lIndex
        if (lIndex > p[1]) p[1] = lIndex
        return p
      }, [1000, -1000])
      // get all layer ids in that range set sel to that list
      const inRange = state.scene.slice(indices[0], indices[1] + 1)
      setSelectedRowKeys(inRange.map((v: any) => v.id))
    } else if (event.metaKey || event.ctrlKey) {
      if (index >= 0) {
        // deselect
        sel.splice(index, 1)
        setSelectedRowKeys(sel)
      } else {
        sel.push(record.id)
        setSelectedRowKeys(sel)
      }
    } // new selection
    else {
      if (selectedRowKeys.length === 1 && selectedRowKeys[0] === record.id) setSelectedRowKeys([])
      else setSelectedRowKeys([record.id])
    }
  }

  const getGroupName = (name: string) => {
    return name.split('::').length > 1 ? name.split('::')[0] : 'Layers'
  }

  return (
    <Col>
      <Collapse 
        // defaultActiveKey={['layers']}
        style={{ 
          border: '1px solid #d0d7de',
          marginBottom: 16, 
        }}
        ghost
      >
        {layers.length > 0 ?
          <>
            {layers.map((l, i, a) => {
              const group = getGroupName(l.name)
          
              // only one collapse per group
              if (a.findIndex(v => getGroupName(v.name) === group) !== i) {
                return (
                  <></>
                )
              }

              return(
                <Collapse.Panel 
                  key={group.toLowerCase().replace(' ', '-')}
                  header={group}
                >
                  <Table columns={columns}
                    style={{ 
                      fontSize: '0.75rem' 
                    }}
                    dataSource={layers.filter(l => getGroupName(l.name) === group)}
                    size='small'
                    rowKey='id'
                    rowSelection={{
                      selectedRowKeys,
                      renderCell: (checked) => (
                        <div className={`pollination-row ${checked ? 'pollination-sel-row' : 'pollination-sel-row-not'}`}>&nbsp;</div>
                      ),
                      columnWidth: '2px',
                    }}
                    showHeader={false}
                    pagination={false}
                    onRow={(record) => {
                      return {
                        onClick: (event) => { 
                          handleRowClick(record, event) 
                        },
                      }
                    }}
                  />
                </Collapse.Panel>
              )
            })}
          </>
          :
          <Collapse.Panel 
            key={'layers'}
            header={'Layers'}
          >
            <Table columns={columns}
              style={{ 
                fontSize: '0.75rem' 
              }}
              dataSource={[]}
              size='small'
              rowKey='id'
              rowSelection={{
                selectedRowKeys,
                renderCell: (checked) => (
                  <div className={`pollination-row ${checked ? 'pollination-sel-row' : 'pollination-sel-row-not'}`}>&nbsp;</div>
                ),
                columnWidth: '2px',
              }}
              showHeader={false}
              pagination={false}
              onRow={(record) => {
                return {
                  onClick: (event) => { 
                    handleRowClick(record, event) 
                  },
                }
              }}
            />

          </Collapse.Panel> 
        }
      </Collapse>
      <VTKLayerProperties state={state}
        ids={selectedRowKeys}
        dispatch={dispatch}
      />
    </Col>
  )
}